import {
  NOTETAKER_ET_TOGGLE_VARIANTS,
  isNotetakerEtToggleEnabled,
} from './upsell_notetaker_in_et_creation'

const DEFAULTS = {
  shouldShow: false,
  defaultEnabled: false,
  isEtInMiIncludeList: false,
  isEtEnabledViaMi: false,
  MI_STATE: {
    miNeverEnabled: true,
    miEnabledAndNotDisabled: false,
    miEnabledButDisabled: false,
  },
}

// MI settings have both v3 (booleans) and v2 (string enums) shapes in the wild.
// Prefer v3 when present, fall back to v2, and default to enabled.
const getAutoRecordCalendlyEnabled = (miSettings) => {
  if (!miSettings) return true

  if (typeof miSettings.autoRecordV3Calendly === 'boolean') {
    return miSettings.autoRecordV3Calendly
  }

  if (typeof miSettings.autoRecordV2Calendly === 'string') {
    return miSettings.autoRecordV2Calendly !== 'none'
  }

  return true
}

// Same v3/v2 fallback for the Calendly include list.
const getMiCalendlyIncludeList = (miSettings) => (
  miSettings?.autoRecordV3CalendlyIncludeList
  || miSettings?.autoRecordV2CalendlyIncludeList
  || []
)

const isEventTypeInMiIncludeList = ({ eventTypeId, miSettings }) => {
  if (!eventTypeId) return false
  const includeList = getMiCalendlyIncludeList(miSettings)
  const eventTypeIdAsString = String(eventTypeId)
  return includeList.some((id) => String(id) === eventTypeIdAsString)
}

// MI user state machine:
// 1) miNeverEnabled: the user has never enabled MI before.
// 2) miEnabledAndNotDisabled: MI is enabled and Calendly auto-record is on.
// 3) miEnabledButDisabled: MI was enabled before but is currently disabled.
const getMiUserState = (miSettings) => {
  if (!miSettings) return DEFAULTS.MI_STATE

  const miEnabled = Boolean(miSettings.miEnabled)
  const userHasEnabledMiBefore = Boolean(miSettings.userHasEnabledMiBefore)
  const autoRecordCalendlyEnabled = getAutoRecordCalendlyEnabled(miSettings)

  const miNeverEnabled = !userHasEnabledMiBefore
  const miEnabledAndNotDisabled = miEnabled && autoRecordCalendlyEnabled
  const miEnabledButDisabled = !miNeverEnabled && !miEnabledAndNotDisabled

  return {
    miNeverEnabled,
    miEnabledAndNotDisabled,
    miEnabledButDisabled,
  }
}

const getVariantDefaultEnabled = (variant) => {
  if (variant === NOTETAKER_ET_TOGGLE_VARIANTS.VARIANT_1) return true
  if (variant === NOTETAKER_ET_TOGGLE_VARIANTS.VARIANT_2) return false
  return false
}

export const getNotetakerEtToggleDefaults = ({ variant, miSettings, eventTypeId }) => {
  if (!variant) return DEFAULTS

  const isEtInMiIncludeList = isEventTypeInMiIncludeList({ eventTypeId, miSettings })
  const miEnabled = Boolean(miSettings?.miEnabled)
  const autoRecordCalendlyEnabled = getAutoRecordCalendlyEnabled(miSettings)
  const miState = getMiUserState(miSettings)
  const isEtEnabledViaMi = miEnabled && autoRecordCalendlyEnabled && isEtInMiIncludeList
  const variantEnabled = isNotetakerEtToggleEnabled(variant)
  const isControl = variant === NOTETAKER_ET_TOGGLE_VARIANTS.CONTROL
  const shouldShow = isControl ? isEtEnabledViaMi : variantEnabled

  if (isEtEnabledViaMi) {
    return {
      shouldShow,
      defaultEnabled: true,
      isEtInMiIncludeList,
      isEtEnabledViaMi,
      miState,
    }
  }

  if (miState.miNeverEnabled) {
    return {
      shouldShow,
      defaultEnabled: getVariantDefaultEnabled(variant),
      isEtInMiIncludeList,
      isEtEnabledViaMi,
      miState,
    }
  }

  if (!miEnabled || !autoRecordCalendlyEnabled) {
    return {
      shouldShow,
      defaultEnabled: false,
      isEtInMiIncludeList,
      isEtEnabledViaMi,
      miState,
    }
  }

  // MI is enabled and auto-recording is on: the include list is the source of truth per ET.
  return {
    shouldShow,
    defaultEnabled: isEtInMiIncludeList,
    isEtInMiIncludeList,
    isEtEnabledViaMi,
    miState,
  }
}
